import React from 'react';
import { theme } from '@frontend/theme';
import { Modal, ModalProps, ModalControlModalProps } from '../../../modal';
import { MultiStepRoot, MultiStepProps } from '../../multi-step.component';
import { useMultiStepModalContext } from './modal.context';
import { MultiStepModalProvider, MultiStepModalProviderProps } from './modal.provider';

export type MultiStepModalProps = MultiStepProps &
  MultiStepModalProviderProps & {
    className?: string;
    modalProps: ModalControlModalProps;
    closeModal?: () => void;
    maxWidth?: ModalProps['maxWidth'];
    minWidth?: ModalProps['minWidth'];
  };

export function MultiStepModal({
  children,

  /**
   * Modal provider props
   */
  disableCloseOnOverlayClick,
  disableCloseOnEscape,
  onChangeDisableOverlayClick,
  onChangeDisableEscape,
  closeModal,

  /**
   * Any/all Multi Step Props
   */
  ...props
}: MultiStepModalProps) {
  return (
    <MultiStepModalProvider
      disableCloseOnOverlayClick={disableCloseOnOverlayClick}
      disableCloseOnEscape={disableCloseOnEscape}
      onChangeDisableOverlayClick={onChangeDisableOverlayClick}
      onChangeDisableEscape={onChangeDisableEscape}
      closeModal={closeModal}
    >
      <MultiStepModalBody {...props}>{children}</MultiStepModalBody>
    </MultiStepModalProvider>
  );
}

type MultiStepModalBodyProps = MultiStepProps & {
  className?: string;
  modalProps: ModalControlModalProps;
  maxWidth?: ModalProps['maxWidth'];
  minWidth?: ModalProps['minWidth'];
};
function MultiStepModalBody({
  children,

  /** Modal props */
  className,
  modalProps,
  maxWidth = parseInt(theme.spacing(82)),
  minWidth = parseInt(theme.spacing(82)),

  /**
   * Any/all Multi Step Props
   */
  onCancel,
  onComplete,
  ...props
}: MultiStepModalBodyProps) {
  const { disableCloseOnEscape, disableCloseOnOverlayClick, closeModal } = useMultiStepModalContext();

  const handleCancel = React.useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    if (closeModal) {
      closeModal();
    }
  }, [onCancel, closeModal]);

  const handleComplete = React.useCallback(() => {
    if (onComplete) {
      onComplete();
    }

    if (closeModal) {
      closeModal();
    }
  }, [onComplete, closeModal]);

  return (
    <Modal
      disableCloseOnEscape={disableCloseOnEscape}
      disableCloseOnOverlayClick={disableCloseOnOverlayClick}
      minWidth={minWidth}
      maxWidth={maxWidth}
      className={className}
      {...modalProps}
    >
      <MultiStepRoot {...props} onComplete={handleComplete} onCancel={handleCancel}>
        {children}
      </MultiStepRoot>
    </Modal>
  );
}
